import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ViewFolderRequest } from 'src/Common/manageViews/interfaces/ViewFolderRequest';
import { convertQueryToBoolean, UATConstants } from 'src/Common/Models/UATConstants';
import { keepUnusedDataForConfig } from 'src/Common/RTK/utils/store-constants';
import { MANAGE_ACTION_CONSTANTS } from 'src/manageAction/utils/manageActionConstants';
import { buildODataQueryString } from '../../utils/BuildOdataQueryString';
import {
  UserMetadata,
  UserMetadataPatchRequest,
  UserMetadataRequest,
  UserMetadataResponse,
} from 'src/Common/manageViews/interfaces/UserMetadataResponse';
import {
  FolderViewResponse,
  FolderViewResponse,
  FolderViewResponseQuery,
} from 'src/Common/manageViews/interfaces/ViewFolderResponse';
import { GetViewsQueryParams } from 'src/Common/manageViews/interfaces/GetViewsQueryParams';
import { OdataQueryParameters } from 'src/Common/manageViews/interfaces/OdataQueryParameters';
import { SearchRequestParameters } from '../../interfaces/HubOverview';
import { ITrackingExperience } from 'src/Components/CaseTracking/CaseTracking.types';

const buildSearchUrl = (params: SearchRequestParameters): string => {
  const queryParams = new URLSearchParams();

  if (params.tenantId) {
    queryParams.append('tenantid', params.tenantId);
  }

  if (params.queryId) {
    queryParams.append('viewId', params.queryId);
  }

  if (params.pageNo !== undefined && params.pageNo !== null) {
    queryParams.append('$skip', params.pageNo.toString());
  }

  if (params.recordPerPage !== undefined && params.recordPerPage !== null) {
    queryParams.append('$top', params.recordPerPage.toString());
  } else {
    queryParams.append('$top', '50');
  }

  if (params.searchText) {
    queryParams.append('$search', encodeURIComponent(params.searchText));
  }

  if (params.sortOrder) {
    queryParams.append('$orderby', encodeURIComponent(params.sortOrder));
  }

  if (params.filterData) {
    queryParams.append('$filter', encodeURIComponent(params.filterData));
  }

  const queryParamsString: string = queryParams.toString();
  return queryParamsString;
};

export const getViewsDataApi = createApi({
  reducerPath: 'getViewsDataApi',
  baseQuery: fetchBaseQuery({
    baseUrl: UATConstants.CommonServicesWebApiUrl,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_API);
      const coreAPIToken = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_CORE_API);
      const graphToken = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_GRAPH);

      if (token && graphToken && coreAPIToken) {
        headers.set('Authorization', `Bearer ${token}`);
        headers.set('Coreapitoken', coreAPIToken);
        headers.set('Graphtoken', graphToken);
      } else {
        console.warn('One or more tokens are missing');
      }
      return headers;
    },
  }),
  tagTypes: ['multiTenantsGetView'],
  endpoints: (builder) => ({
    getViewData: builder.query<any, any>({
      query: (tenantId) => ({
        method: 'GET',
        url: UATConstants.ApiPaths.GetViewsList + `?tenantId=${tenantId}`,
      }),
      providesTags: (result, error, tenantId) => [{ type: 'multiTenantsGetView', tenantId }],
      keepUnusedDataFor: keepUnusedDataForConfig,
    }),
    getTenantActionData: builder.query<any[], SearchRequestParameters>({
      query: (data: SearchRequestParameters) => {
        const baseUrl = UATConstants.ApiPaths.RaidItemsAPIPath;
        const tenantIdQuery = `tenantid=${data.tenantId}`;
        const queryIdQuery = data.queryId ? `&viewId=${data.queryId}` : '';
        const skipQuery = `&$skip=${data.pageNo}`;
        const orderByQuery = `&$orderby=${encodeURIComponent(data.sortOrder)}`;
        const topQuery = `&$top=${data.recordPerPage}`;
        const queryDataSet = data?.filterData && convertQueryToBoolean(data?.filterData);
        const filterQuery = queryDataSet ? `&$filter=${encodeURIComponent(queryDataSet)}` : '';
        const searchQuery = data?.searchText
          ? `&$search=${encodeURIComponent(data?.searchText)}`
          : ``;
        const url = `${baseUrl}?${tenantIdQuery}${queryIdQuery}${skipQuery}${orderByQuery}${topQuery}${filterQuery}${searchQuery}`;
        const body = data?.RoBTenantRolesScopes
          ? { ...data.body, RoleScopeMap: data.RoBTenantRolesScopes }
          : data.body;

        return {
          method: 'POST',
          url: url,
          body,
        };
      },
    }),
    // getExportAllTenantData API call is intended to export up to a maximum of 1500 records from the tenant
    getExportAllTenantData: builder.query<any[], SearchRequestParameters>({
      query: (data: SearchRequestParameters) => {
        const baseUrl = UATConstants.ApiPaths.RaidItemsAPIPath;
        const tenantIdQuery = `tenantid=${data.tenantId}`;
        const queryIdQuery = data.queryId ? `&viewId=${data.queryId}` : '';
        const skipQuery = `&$skip=${data.pageNo}`;
        const orderByQuery = `&$orderby=${encodeURIComponent(data.sortOrder)}`;
        const topQuery = `&$top=${data.recordPerPage}`;
        const queryDataSet = data?.filterData && convertQueryToBoolean(data?.filterData);
        const filterQuery = queryDataSet ? `&$filter=${encodeURIComponent(queryDataSet)}` : '';
        const searchQuery = data?.searchText
          ? `&$search=${encodeURIComponent(data?.searchText)}`
          : ``;
        const url = `${baseUrl}?${tenantIdQuery}${queryIdQuery}${skipQuery}${orderByQuery}${topQuery}${filterQuery}${searchQuery}`;

        const body = data?.RoBTenantRolesScopes
          ? { ...data.body, RoleScopeMap: data.RoBTenantRolesScopes }
          : data.body;

        return {
          method: 'POST',
          url: url,
          body,
        };
      },
    }),
    getTenantSearchData: builder.query<any, any>({
      query: (data) => {
        //const url: string = `${UATConstants.ApiPaths.RaidItemsAPIPath}?queryid=${data.queryId}&tenantid=${data.tenantId}&$skip=${data.pageNo}&$search=${data.searchText}&$orderby=${data.sortOrder}&$top=${data.recordPerPage}${data?.filterData ? `&$filter=${data.filterData}` : ''}`;
        const url: string = `${UATConstants.ApiPaths.RaidItemsAPIPath}?${buildSearchUrl(data)}`;
        const body = data?.RoBTenantRolesScopes
          ? { ...data.body, RoleScopeMap: data.RoBTenantRolesScopes }
          : data.body;

        return {
          method: 'POST',
          url,
          body,
        };
      },
    }),
    getArtemisRelatedData: builder.query<any, any>({
      query: (data) => {
        const body = data?.RoBTenantRolesScopes
          ? { ...data.body, RoleScopeMap: data.RoBTenantRolesScopes }
          : data.body;
        return {
          method: 'POST',
          url: `${UATConstants.ApiPaths.RaidItemsAPIPath}?${buildSearchUrl(data)}`,
          body,
        };
      },
    }),
    getTenantRelatedData: builder.query<any, any>({
      query: (data) => {
        const body = data?.RoBTenantRolesScopes
          ? { ...data.body, RoleScopeMap: data.RoBTenantRolesScopes }
          : data.body;
        return {
          method: 'POST',
          url: `${UATConstants.ApiPaths.RaidItemsAPIPath}?${buildSearchUrl(data)}`,
          body,
        };
      },
    }),
    createView: builder.mutation<any, ViewFolderRequest>({
      query: (data) => ({
        method: 'POST',
        url: UATConstants.ApiPaths.View,
        body: data,
      }),
    }),
    updateView: builder.mutation<
      any,
      { id: string; data: Partial<ViewFolderRequest>; tenantId: string }
    >({
      query: ({ id, data, tenantId }) => ({
        method: 'PATCH',
        url: `${UATConstants.ApiPaths.View}/${id}?tenantId=${tenantId}`,
        body: data,
      }),
    }),
    shareView: builder.mutation<FolderViewResponse, { id: string; tenantId: string }>({
      query: ({ id, tenantId }) => ({
        method: 'POST',
        url: `${UATConstants.ApiPaths.View}/${id}/share?tenantId=${tenantId}`,
        body: {},
      }),
    }),
    shareViewWithMe: builder.mutation<FolderViewResponse, { id: string; tenantId: string }>({
      query: ({ id, tenantId }) => ({
        method: 'POST',
        url: `${UATConstants.ApiPaths.View}/${id}/sharewithupn?tenantId=${tenantId}`,
        body: {},
      }),
    }),
    createFolderView: builder.mutation<any, ViewFolderRequest>({
      query: (data) => ({
        method: 'POST',
        url: UATConstants.ApiPaths.FolderView,
        body: data,
      }),
    }),
    updateFolderView: builder.mutation<
      any,
      { id: string; data: ViewFolderRequest; tenantId: string }
    >({
      query: ({ id, data, tenantId }) => ({
        method: 'PATCH',
        url: `${UATConstants.ApiPaths.FolderView}/${id}?tenantId=${tenantId}`,
        body: data,
      }),
    }),
    getFolderById: builder.query<any, { id: string; tenantId: string }>({
      query: ({ id, tenantId }) => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.FolderView}/${id}?tenantId=${tenantId}`,
      }),
    }),
    getViewById: builder.query<any, { id: string; tenantId: string }>({
      query: ({ id, tenantId }) => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.View}/${id}?tenantId=${tenantId}`,
      }),
    }),
    deleteView: builder.mutation<any, { id: string; tenantId: string }>({
      query: ({ id, tenantId }) => ({
        method: 'DELETE',
        url: `${UATConstants.ApiPaths.View}/${id}?tenantId=${tenantId}`,
      }),
    }),
    deleteFolder: builder.mutation<any, { id: string; tenantId: string }>({
      query: ({ id, tenantId }) => ({
        method: 'DELETE',
        url: `${UATConstants.ApiPaths.FolderView}/${id}?tenantId=${tenantId}&cascade=true`,
      }),
    }),
    getViews: builder.query<FolderViewResponse[], GetViewsQueryParams>({
      query: (data: GetViewsQueryParams) => {
        const queryString = buildODataQueryString({
          tenantId: data.tenantId,
          $filter: data.filter,
          $orderby: data.orderBy,
          $top: data.top || 50,
          $skip: data.skip,
        });
        return {
          method: 'GET',
          url: `${UATConstants.ApiPaths.View}${queryString}`,
        };
      },
      transformResponse: (response: FolderViewResponseQuery): FolderViewResponse[] => {
        return response.value;
      },
    }),
    getFolders: builder.query<FolderViewResponse[], OdataQueryParameters>({
      query: (data) => {
        const queryString = buildODataQueryString({
          tenantId: data.tenantId,
          $filter: data.filter,
          $orderby: data.orderBy,
          $top: data.top,
          $skip: data.skip,
        });

        return {
          method: 'GET',
          url: `${UATConstants.ApiPaths.FolderView}${queryString}`,
        };
      },
      transformResponse: (response: FolderViewResponseQuery): FolderViewResponse[] => {
        return response.value;
      },
    }),
    getUserMetadata: builder.query<UserMetadata[], any>({
      query: (tenantId) => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.UserMetadata}?tenantId=${tenantId}`,
      }),
      transformResponse: (response: UserMetadataResponse): UserMetadata[] => {
        return response.value;
      },
    }),
    createUserMetadata: builder.mutation<any, UserMetadataRequest>({
      query: (data) => ({
        method: 'POST',
        url: `${UATConstants.ApiPaths.UserMetadata}`,
        body: data,
      }),
    }),
    updateUserMetadata: builder.mutation<
      any,
      { id: string; data: UserMetadataPatchRequest; tenantId: string }
    >({
      query: ({ id, data, tenantId }) => ({
        method: 'PATCH',
        url: `${UATConstants.ApiPaths.UserMetadata}/${id}?tenantId=${tenantId}`,
        body: data,
      }),
    }),
    favoriteView: builder.mutation<any, UserMetadataPatchRequest>({
      query: (data) => ({
        method: 'PATCH',
        url: `${UATConstants.ApiPaths.UserMetadata}/${data.id}`,
        body: data,
      }),
    }),
    unfavoriteView: builder.mutation<any, UserMetadataPatchRequest>({
      query: (data) => ({
        method: 'PATCH',
        url: `${UATConstants.ApiPaths.UserMetadata}/${data.id}`,
        body: data,
      }),
    }),
    toggleDefaultView: builder.mutation<any, UserMetadataPatchRequest>({
      query: (data, tenantId) => ({
        method: 'PATCH',
        url: `${UATConstants.ApiPaths.UserMetadata}/${data.id}?tenantId=${data.tenantId}`,
        body: data,
      }),
    }),
    getTenantBulkEdit: builder.query<any, any>({
      query: (data) => ({
        method: 'POST',
        url: UATConstants.ApiPaths.BulkEditAPIPath,
        body: data,
      }),
    }),
    getTenantBulkDelete: builder.query<any, any>({
      query: (data) => ({
        method: 'POST',
        url: UATConstants.ApiPaths.BulkDeleteAPIPath,
        body: data,
      }),
    }),
    getAMMDashboardTaskList: builder.query<any, any>({
      query: (data) => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.GetAMMDashboardTasks}?viewName=${data?.taskViewName}&pageNumber=${data?.pageNumber}`,
      }),
    }),
    getAMMProjectTracking: builder.query<ITrackingExperience, any>({
      query: (data) => ({
        method: 'GET',
        url: `${UATConstants.ApiPaths.GetAMMProjectTracking}${data?.actionId}`,
      }),
    }),
  }),
});

export const {
  useGetViewDataQuery,
  useGetTenantActionDataQuery,
  useLazyGetTenantActionDataQuery,
  useLazyGetExportAllTenantDataQuery,
  useGetTenantSearchDataQuery,
  useGetArtemisRelatedDataQuery,
  useGetTenantFilterDataQuery,
  useGetTenantRelatedDataQuery,
  useCreateViewMutation,
  useUpdateViewMutation,
  useShareViewMutation,
  useShareViewWithMeMutation,
  useCreateFolderViewMutation,
  useUpdateFolderViewMutation,
  useDeleteViewMutation,
  useDeleteFolderMutation,
  useGetViewByIdQuery,
  useLazyGetViewByIdQuery,
  useLazyGetFolderByIdQuery,
  useGetViewsQuery,
  useLazyGetViewsQuery,
  useLazyGetFoldersQuery,
  useGetUserMetadataQuery,
  useUpdateUserMetadataMutation,
  useFavoriteViewMutation,
  useUnfavoriteViewMutation,
  useToggleDefaultViewMutation,
  useCreateUserMetadataMutation,
  useLazyGetAMMDashboardTaskListQuery,
  useLazyGetAMMProjectTrackingQuery
} = getViewsDataApi;
